import { clubEffectiveCourts } from '../lib/clubCourts';
import { createMemberBooking } from '../lib/api/member';
import { playerSurfaceActive } from '../lib/api/customerContext';
import {
  BookingRefusedError,
  bookingsAreServerAuthoritative,
  createBookings,
  firstRefusal,
  firstRefusalMessage,
  patchBooking,
  type CreateBookingsResult,
} from '../lib/api/bookings';
import { OverrideRefusedError, sendsOverride } from '../lib/api/bookingOverride';
import { translate, DEFAULT_LOCALE, type MessageKey, type Vars } from '../i18n';

// ─────────────────────────────────────────────────────────────────────
// useBookingActions — the engine/config-coupled court-booking write handlers
// lifted verbatim out of Padel24AppMain (extracted from the former monolith)
// as a god-component collapse slice (PR-BA). addBooking / addManyBookings /
// updateBooking / replaceSeries. The simpler booking mutators (persistBookings
// / deleteBooking / deleteManyBookings / syncBookingsFromPanel) live in
// useBookings — these four stay coupled to the live Padel24Engine.booking
// engine + bookingConfig.
//
// R5 (booking ref-mirror): each handler reads `bookingsRef.current || bookings`
// (the synchronous mirror, fresh under rapid successive calls), and on success
// updates `bookingsRef.current` BEFORE `setBookings` — preserved verbatim.
// Storage-first via the injected `persistBookings` (await before setState).
// Padel24Engine is passed in (not imported) so the .booking.* call-sites stay
// verbatim. Deps are all available early in Padel24AppMain (useStore), so this
// hook is called before topUpRollingSeries (which calls addManyBookings).
// ─────────────────────────────────────────────────────────────────────
export interface UseBookingActionsDeps {
  bookingsRef: any;
  bookings: any;
  setBookings: (v: any) => void;
  persistBookings: (list: any) => Promise<any>;
  bookingConfig: any;
  clubInfo: any;
  Padel24Engine: any;
  /**
   * After a court booking is PLACED anew (moved or re-timed). The server moves
   * the equipment riding with it in the same transaction (#914), so the
   * browser's copy of the equipment lanes is re-read rather than left at the
   * old time until a reload.
   */
  onBookingPlaced?: () => Promise<void>;
  /**
   * The machines a MEMBER booked with a court (#911 slice 2), as the server
   * wrote them. Added to the equipment slice so the booking screen stops
   * offering them at once, rather than after the next bootstrap.
   */
  onMemberAssetBookings?: (rows: Record<string, unknown>[]) => void;
  /**
   * May this session ask for `adminOverride` (#949)? From the `/me` union,
   * through `overrideRight` (lib/api/bookingOverride.ts). Only a known
   * `false` withholds the flag; unknown sends it as asked and lets the
   * server decide, which is what every caller did before.
   */
  mayOverrideConstraints?: boolean | null;
  /** The translator for the sentences the override produces; Bokmål by default. */
  t?: (key: MessageKey, vars?: Vars) => string;
}

const bokmal = (key: MessageKey, vars?: Vars) => translate(DEFAULT_LOCALE, key, vars);

export function useBookingActions({
  bookingsRef,
  bookings,
  setBookings,
  persistBookings,
  bookingConfig,
  clubInfo,
  Padel24Engine,
  onBookingPlaced,
  onMemberAssetBookings,
  mayOverrideConstraints,
  t = bokmal,
}: UseBookingActionsDeps) {
  // ── THE OVERRIDE IS ASKED FOR ONLY BY WHOEVER HOLDS IT (#949) ──────
  //
  // Tournaments, KM, activities and the calendar's admin mode ask for
  // `adminOverride`; the server refuses the whole request from a session
  // without `booking.override_constraints`. So a caller's ask is honoured here
  // only when the session may make it, and otherwise the booking goes as an
  // ordinary one that the club's rules decide. What a holder sends is
  // unchanged, byte for byte.
  const overrideFor = (asked: boolean) => {
    const send = sendsOverride(asked, mayOverrideConstraints);
    return { send, withheld: asked && !send };
  };
  // The calendar refusals a withheld override leaves behind, said as what they
  // are: the club's rules, which this person may not override. An
  // out-of-service court keeps its own sentence — it is not a rule.
  const withheldNotice = (out: CreateBookingsResult, total: number): string | null => {
    const ruleRefusal = (out.refusals ?? []).find((r) => r.error === 'invalid');
    if (!ruleRefusal || out.failed < 1) return null;
    return t('booking.override.withheld', {
      count: out.failed,
      total,
      reason: ruleRefusal.reason || '–',
    });
  };
  // A REPLACEMENT IS NOT SENT WITHOUT THE OVERRIDE IT ASKED FOR (💬13 M1).
  // The deletions in a batch (`deleteIds`, a series replacement) are applied
  // unconditionally by the server, and the creations are then judged one by
  // one — so a re-reservation sent under the club's rules could remove the old
  // courts and fail to place the new ones. When the override is withheld from
  // such a request, nothing is sent and nothing changes; the person is told
  // why, in the same words as the server's own refusal.
  const replacementRefused = () => ({
    success: false,
    error: t('booking.override.refused'),
    code: 'override_not_held' as const,
    created: 0,
    failed: 0,
  });
  // The whole request refused for the override itself: `/me` had not
  // answered, or the right was narrowed since it did.
  const overrideRefused = (e: unknown) =>
    e instanceof OverrideRefusedError
      ? { error: t('booking.override.refused'), code: e.code }
      : null;
  const addBooking = async (
    bookingData: any,
    {
      bufferMin,
      adminOverride: askedOverride = false,
      overrideCourtOutOfService = false,
    }: any = {},
  ) => {
    const { send: adminOverride, withheld } = overrideFor(askedOverride);
    // Use bookingsRef.current — it is updated synchronously on every setBookings
    // (see the useEffect further down in the component). The ordinary closure
    // `bookings` is stale under rapid successive calls (e.g. the demo generator).
    const baseList = bookingsRef.current || bookings;
    // The shared answer — see clubEffectiveCourts. This used to pass null
    // and let the engine count 1..clubInfo.courts, the CENTRE's physical
    // number: a four-court club got a fifth court nobody has, and on THIS
    // path that court decides whether a booking is accepted at all.
    const enabledCourts = clubEffectiveCourts(bookingConfig, clubInfo);
    // adminOverride: admin books freely — only direct overlaps are blocked.
    // (bufferMin 0 + smartAllocation false ⇒ only the overlap check in validateBooking.)
    // minBookingMin = shortest allowed duration; controls the dead-gap threshold.
    const minBookingMin =
      bookingConfig.allowedDurations && bookingConfig.allowedDurations.length > 0
        ? Math.min(...bookingConfig.allowedDurations)
        : 60;
    // A MEMBER BOOKS THROUGH THE MEMBER COMMAND (#185).
    //
    // Checked BEFORE the club path, because a member reaching `/bookings` is
    // simply refused — this is not an optimisation. The two differ in what they
    // trust: the club command takes a calendar the client read earlier and an
    // `adminOverride`, while `/member/bookings` derives everything from the
    // member's own session and refuses what a member may not do — a slot the
    // slot-finder would not have offered, one in the past, one beyond the
    // booking window.
    //
    // `adminOverride` and the out-of-service override are deliberately NOT
    // forwarded. They are operator powers; a member holding one would be the
    // bug, and silently dropping them here is the only correct thing to do with
    // arguments this path must never honour.
    // THE SURFACE, NOT THE CREDENTIAL FAMILY (#800). This is the sharpest of
    // the five: a player whose surface was read as the CLUB's would write
    // through `POST /bookings`, which honours the `paymentStatus` the client
    // sends — so a priced court lands `free` and the payment sheet never
    // appears. `customerContext`'s own header names that outcome: «money
    // silently not owed is a worse outcome than an error, and nothing failed
    // anywhere to say so».
    if (playerSurfaceActive()) {
      const out = await createMemberBooking({
        court: bookingData.court,
        date: bookingData.date,
        startTime: bookingData.startTime,
        endTime: bookingData.endTime,
        durationMin: bookingData.durationMin,
        notes: bookingData.notes,
        // The machines ticked beside the court (#911). Ids only — the server
        // prices each from its own zones and books it in the same command.
        assetIds: Array.isArray(bookingData.assetIds) ? bookingData.assetIds : undefined,
        // PRICE AND PAYMENT STATUS ARE NOT SENT. The server computes the price
        // from the club's zones and sets the status itself; the booking screen
        // shows a preview with the same shared function, so the number a member
        // saw is the number they get. Forwarding them here would be the client
        // declaring what it owes.
      });
      if (!out.success) {
        return { success: false, error: out.error, code: out.code, assetId: out.assetId };
      }
      const created = out.booking ?? bookingData;
      const newList = [...baseList, created];
      bookingsRef.current = newList;
      setBookings(newList);
      const assetBookings = out.assetBookings ?? [];
      if (assetBookings.length) onMemberAssetBookings?.(assetBookings);
      return { success: true, booking: created, assetBookings };
    }

    // SERVER-AUTHORITATIVE PATH (#245). The command reads the calendar, runs
    // the same engine validation and writes one row, so nothing here depends on
    // a list read earlier. The legacy path below is unchanged — it is what
    // the in-memory backend still runs.
    if (bookingsAreServerAuthoritative()) {
      try {
        const out = await createBookings({
          bookings: [bookingData],
          adminOverride,
          bufferMin,
          overrideCourtOutOfService,
        });
        if (out.created === 0) {
          // The server says WHY, so the operator is not left guessing — an
          // out-of-service court reads as one rather than as «rejected» (#271).
          //
          // The refusal travels as an OBJECT as well as a sentence: whether the
          // caller may override is the server's answer, and the screen offering
          // the override must not compute it for itself.
          return {
            success: false,
            error:
              (withheld ? withheldNotice(out, 1) : null) ||
              firstRefusalMessage(out) ||
              'Bookingen ble avvist',
            refusal: firstRefusal(out),
          };
        }
        const newList = [...baseList, ...out.bookings];
        bookingsRef.current = newList;
        setBookings(newList);
        return { success: true, booking: out.bookings[0] };
      } catch (e: any) {
        const refused = overrideRefused(e);
        if (refused) return { success: false, ...refused };
        return { success: false, error: e?.message || 'ukjent feil' };
      }
    }

    const result = Padel24Engine.booking.createBooking(
      bookingData,
      baseList,
      adminOverride
        ? { bufferMin: 0, smartAllocation: false }
        : {
            bufferMin:
              bufferMin ?? bookingConfig.bufferMin ?? Padel24Engine.booking.DEFAULT_BUFFER_MIN,
            minBookingMin,
            smartAllocation: bookingConfig.smartAllocation !== false,
            totalCourts: enabledCourts.length,
            enabledCourts,
          },
    );
    if (!result.ok) return { success: false, error: result.reason };
    const newList = [...baseList, result.booking];
    try {
      await persistBookings(newList);
      bookingsRef.current = newList; // keep the ref synchronized BEFORE setState
      setBookings(newList);
      return { success: true, booking: result.booking };
    } catch (e: any) {
      return { success: false, error: e.message };
    }
  };

  const addManyBookings = async (
    bookingDataList: any,
    { adminOverride: askedOverride = false, deleteIds = [] }: any = {},
  ) => {
    const { send: adminOverride, withheld } = overrideFor(askedOverride);
    if (withheld && deleteIds.length > 0) return replacementRefused();
    let baseList = bookingsRef.current || bookings;
    if (deleteIds.length > 0) {
      const del = new Set(deleteIds);
      baseList = baseList.filter((b) => !del.has(b.id));
    }
    // The shared answer — see clubEffectiveCourts. This used to pass null
    // and let the engine count 1..clubInfo.courts, the CENTRE's physical
    // number: a four-court club got a fifth court nobody has, and on THIS
    // path that court decides whether a booking is accepted at all.
    const enabledCourts = clubEffectiveCourts(bookingConfig, clubInfo);
    const minBookingMin =
      bookingConfig.allowedDurations && bookingConfig.allowedDurations.length > 0
        ? Math.min(...bookingConfig.allowedDurations)
        : 60;
    const opts = adminOverride
      ? { bufferMin: 0, smartAllocation: false }
      : {
          bufferMin: bookingConfig.bufferMin ?? Padel24Engine.booking.DEFAULT_BUFFER_MIN,
          minBookingMin,
          smartAllocation: bookingConfig.smartAllocation !== false,
          totalCourts: enabledCourts.length,
          enabledCourts,
        };
    if (bookingsAreServerAuthoritative()) {
      // ONE request: the deletes and the creates land in one transaction, so a
      // batch cannot half-apply the way two round trips could.
      try {
        const out = await createBookings({
          bookings: bookingDataList,
          deleteIds: deleteIds.map(String),
          adminOverride,
        });
        const del = new Set(deleteIds.map(String));
        const newList = [
          ...(bookingsRef.current || bookings).filter((b: any) => !del.has(String(b.id))),
          ...out.bookings,
        ];
        bookingsRef.current = newList;
        setBookings(newList);
        // `notice` is set only when a withheld override is WHY something
        // failed, so a screen shows it instead of «opptatt».
        const notice = withheld ? withheldNotice(out, bookingDataList.length) : null;
        return {
          success: true,
          created: out.created,
          failed: out.failed,
          ...(notice ? { notice } : {}),
        };
      } catch (e: any) {
        const refused = overrideRefused(e);
        if (refused) return { success: false, ...refused, created: 0, failed: 0 };
        return { success: false, error: e?.message || 'ukjent feil', created: 0, failed: 0 };
      }
    }

    // Accumulate into a local list so each new booking is validated against the
    // preceding ones in the same batch (without writing to Storage along the way).
    const working = [...baseList];
    let created = 0,
      failed = 0;
    for (const data of bookingDataList) {
      const result = Padel24Engine.booking.createBooking(data, working, opts);
      if (result.ok) {
        working.push(result.booking);
        created++;
      } else failed++;
    }
    if (created === 0 && deleteIds.length === 0) return { success: true, created: 0, failed };
    try {
      await persistBookings(working); // ONE write
      bookingsRef.current = working;
      setBookings(working); // ONE re-render
      return { success: true, created, failed };
    } catch (e: any) {
      return { success: false, error: e.message, created: 0, failed };
    }
  };

  const updateBooking = async (
    bookingId: any,
    patch: any,
    {
      bufferMin,
      adminOverride: askedOverride = false,
      overrideCourtOutOfService = false,
    }: any = {},
  ) => {
    const { send: adminOverride, withheld } = overrideFor(askedOverride);
    const baseList = bookingsRef.current || bookings;
    const existing = baseList.find((b) => b.id === bookingId);
    if (!existing) return { success: false, error: 'Booking ikke funnet' };
    const candidate = { ...existing, ...patch, id: bookingId };
    // If endTime is not specified but durationMin is, calculate it
    if (!patch.endTime && patch.durationMin) {
      candidate.endTime = Padel24Engine.booking.fromMin(
        Padel24Engine.booking.toMin(candidate.startTime) + patch.durationMin,
      );
    }
    // The shared answer — see clubEffectiveCourts. This used to pass null
    // and let the engine count 1..clubInfo.courts, the CENTRE's physical
    // number: a four-court club got a fifth court nobody has, and on THIS
    // path that court decides whether a booking is accepted at all.
    const enabledCourts = clubEffectiveCourts(bookingConfig, clubInfo);
    const minBookingMin =
      bookingConfig.allowedDurations && bookingConfig.allowedDurations.length > 0
        ? Math.min(...bookingConfig.allowedDurations)
        : 60;
    if (bookingsAreServerAuthoritative()) {
      try {
        const saved = await patchBooking(String(bookingId), {
          ...patch,
          adminOverride,
          ...(overrideCourtOutOfService ? { overrideCourtOutOfService } : {}),
          ...(bufferMin != null ? { bufferMin } : {}),
        });
        const newList = baseList.map((b: any) => (b.id === bookingId ? saved : b));
        bookingsRef.current = newList;
        setBookings(newList);
        const placed = ['date', 'startTime', 'endTime', 'durationMin', 'court'].some(
          (k) => k in patch,
        );
        // Best effort: the court move itself succeeded either way.
        if (placed && onBookingPlaced) await onBookingPlaced().catch(() => {});
        return { success: true };
      } catch (e: any) {
        // A move is refused for the same reasons a create is, so it carries the
        // same structure — otherwise the override could be offered on one and
        // not the other, for the identical refusal (#271).
        const refused = overrideRefused(e);
        if (refused) return { success: false, ...refused, refusal: null };
        const refusal = e instanceof BookingRefusedError ? e.refusal : null;
        return {
          success: false,
          error:
            (withheld && refusal?.error === 'invalid'
              ? withheldNotice({ created: 0, failed: 1, bookings: [], refusals: [refusal] }, 1)
              : null) ||
            e?.message ||
            'ukjent feil',
          refusal,
        };
      }
    }

    const v = Padel24Engine.booking.validateBooking(
      candidate,
      baseList,
      adminOverride
        ? { bufferMin: 0, smartAllocation: false }
        : {
            bufferMin:
              bufferMin ?? bookingConfig.bufferMin ?? Padel24Engine.booking.DEFAULT_BUFFER_MIN,
            minBookingMin,
            smartAllocation: bookingConfig.smartAllocation !== false,
            totalCourts: enabledCourts.length,
            enabledCourts,
          },
    );
    if (!v.ok) return { success: false, error: v.reason };
    const newList = baseList.map((b) => (b.id === bookingId ? candidate : b));
    try {
      await persistBookings(newList);
      bookingsRef.current = newList;
      setBookings(newList);
      return { success: true, booking: candidate };
    } catch (e: any) {
      return { success: false, error: e.message };
    }
  };

  const replaceSeries = async (
    removeSeriesId: any,
    newBookingDataList: any,
    { adminOverride: askedOverride = true }: any = {},
  ) => {
    const { send: adminOverride, withheld } = overrideFor(askedOverride);
    // Always a replacement: the whole series goes before the new one is judged.
    if (withheld) return replacementRefused();
    if (bookingsAreServerAuthoritative()) {
      // Removing the old series and creating the new one is ONE intent — two
      // requests could leave the club with neither.
      try {
        const out = await createBookings({
          bookings: newBookingDataList,
          replaceSeriesId: String(removeSeriesId),
          adminOverride,
        });
        const newList = [
          ...(bookingsRef.current || bookings).filter((b: any) => b.seriesId !== removeSeriesId),
          ...out.bookings,
        ];
        bookingsRef.current = newList;
        setBookings(newList);
        const notice = withheld ? withheldNotice(out, newBookingDataList.length) : null;
        return {
          success: true,
          created: out.created,
          failed: out.failed,
          ...(notice ? { notice } : {}),
        };
      } catch (e: any) {
        const refused = overrideRefused(e);
        if (refused) return { success: false, ...refused, created: 0, failed: 0 };
        return { success: false, error: e?.message || 'ukjent feil', created: 0, failed: 0 };
      }
    }

    let baseList = bookingsRef.current || bookings;
    // Remove all old members of the series.
    baseList = baseList.filter((b) => b.seriesId !== removeSeriesId);
    // The shared answer — see clubEffectiveCourts. This used to pass null
    // and let the engine count 1..clubInfo.courts, the CENTRE's physical
    // number: a four-court club got a fifth court nobody has, and on THIS
    // path that court decides whether a booking is accepted at all.
    const enabledCourts = clubEffectiveCourts(bookingConfig, clubInfo);
    const minBookingMin =
      bookingConfig.allowedDurations && bookingConfig.allowedDurations.length > 0
        ? Math.min(...bookingConfig.allowedDurations)
        : 60;
    const opts = adminOverride
      ? { bufferMin: 0, smartAllocation: false }
      : {
          bufferMin: bookingConfig.bufferMin ?? Padel24Engine.booking.DEFAULT_BUFFER_MIN,
          minBookingMin,
          smartAllocation: bookingConfig.smartAllocation !== false,
          totalCourts: enabledCourts.length,
          enabledCourts,
        };
    const working = [...baseList];
    let created = 0,
      failed = 0;
    for (const data of newBookingDataList) {
      const result = Padel24Engine.booking.createBooking(data, working, opts);
      if (result.ok) {
        working.push(result.booking);
        created++;
      } else failed++;
    }
    try {
      await persistBookings(working);
      bookingsRef.current = working;
      setBookings(working);
      return { success: true, created, failed };
    } catch (e: any) {
      return { success: false, error: e.message, created: 0, failed };
    }
  };

  return { addBooking, addManyBookings, updateBooking, replaceSeries };
}
